import { AdminRoles, adminRolesArray } from "@/config/adminRoles"
import { IUserRole } from "@/modules/users/domain/IUser";

export function userIsAllowed(userRoles: IUserRole[]): boolean {
    return userRoles.some((role: IUserRole) => adminRolesArray.includes(role.id))
}

/**
 * La cuenta es de un médico y de nada más.
 *
 * `userIsAllowed` no alcanza para distinguirlo: `adminRolesArray` es
 * `Object.values(AdminRoles)`, o sea **todos** los roles incluido `DOCTOR`, así
 * que sólo rechaza a quien no tiene ninguno. Un médico entraba al portal de
 * gestión sin que nada se lo impidiera.
 *
 * Se mira que **no tenga ningún otro** rol, no que tenga `DOCTOR`: quien es
 * médico y además supervisor —`SUPERVISOR_MEDICO` es justamente eso— trabaja en
 * los dos lados y tiene que poder entrar acá.
 */
export function esCuentaSoloDeMedico(userRoles: IUserRole[] | null): boolean {
    if (!userRoles || userRoles.length === 0) return false
    return userRoles.every((role: IUserRole) => role.id === AdminRoles.DOCTOR)
}

export function userHasRole(userRoles: IUserRole[] | null, roleId: AdminRoles): boolean {
    if (!userRoles) return false
    return userRoles.some((role: IUserRole) => role.id === roleId)
}

export function generateRandomPassword(): string {
    const allowedCharacters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const specialCharacters = '!@#$%^&*_+-=<>?';
    const maxSpecialCharacters = 3;
    const length = 12;
  
    let password = '';
    let specialCount = 0;
  
    for (let i = 0; i < length; i++) {
      let character = '';
  
      if (i < length - maxSpecialCharacters && Math.random() < 0.25 && specialCount < maxSpecialCharacters) {
        character = specialCharacters.charAt(Math.floor(Math.random() * specialCharacters.length));
        specialCount++;
      } else {
        character = allowedCharacters.charAt(Math.floor(Math.random() * allowedCharacters.length));
      }
  
      password += character;
    }
  
    return password;
  }

export function objectToQueryParams(object: Record<string, string | number | null>) {
    const queryParams = Object.keys(object).map(key => {
        return `${key}=${encodeURIComponent(object[key] ?? '')}`;
      }).join('&');
    
    return queryParams;
}

export function removeEmptyValuesFromObject(object: any) {
  const objectToClean = {...object}
  Object.keys(objectToClean).forEach(key => {
    if (objectToClean[key] === null || objectToClean[key] === undefined || objectToClean[key] === '') {
      delete objectToClean[key];
    }
  });

  return objectToClean
}

export function objectsAreEquals(object1: any, object2: any) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      areObjects && !objectsAreEquals(val1, val2) ||
      !areObjects && val1 !== val2
    ) {
      return false;
    }
  }

  return true;
}

function isObject(object: any) {
  return object != null && typeof object === 'object';
}
